// ─── LGM Brand Triad ────────────────────────────────────────────────────────
// Canonical values from lgmexplorer brochures and public-facing pages, confirmed
// against Explorer's own brand-palettes-two-entities memory (2026-06-15 site sampling).
// Do NOT alter these without design sign-off.
export const LGM_ORANGE = '#F15A22';
export const LGM_ORANGE_DARK = '#D6451A';
export const LGM_ORANGE_TINT = '#FFF1EB';

// Wordmark ONLY — never in app glyphs/buttons/charts (Explorer brand rule, aligned 2026-07-06).
export const LGM_MAGENTA = '#9E1C80';
export const LGM_MAGENTA_TINT = '#F8EDF5';

export const LGM_TEAL = '#1C9FB8';
export const LGM_TEAL_DARK = '#16809A';
export const LGM_TEAL_TINT = '#A7D2DC'; // "Sky Teal" — confirmed via Explorer site sampling

// ─── Neutral / Surface ──────────────────────────────────────────────────────
export const LGM_INK = '#2B2B2B'; // "Charcoal" — confirmed via Explorer site sampling
export const LGM_SLATE = '#3C4757'; // body text — still best-guess
export const LGM_MUTED = '#6B7787'; // labels, secondary text — still best-guess
export const LGM_LINE = '#E3E8EF'; // borders, dividers — still best-guess
export const LGM_PANEL = '#ECEFF1'; // "Cloud" — confirmed via Explorer site sampling
export const LGM_BG = '#FFFFFF'; // page background — still best-guess

// ─── Semantic ────────────────────────────────────────────────────────────────
export const COLOR_SUCCESS = '#22c55e';
export const COLOR_SUCCESS_BG = '#dcfce7';
export const COLOR_SUCCESS_TEXT = '#166534';

export const COLOR_WARNING = '#f59e0b';
export const COLOR_WARNING_BG = '#fef3c7';
export const COLOR_WARNING_TEXT = '#92400e';

export const COLOR_DANGER = '#ef4444';
export const COLOR_DANGER_BG = '#fee2e2';
export const COLOR_DANGER_TEXT = '#991b1b';

export const COLOR_INFO = '#3b82f6';
export const COLOR_INFO_BG = '#dbeafe';
export const COLOR_INFO_TEXT = '#1e40af';

// Chart-only purple accent — distinct from brand LGM_MAGENTA (wordmark-exclusive, see above).
export const CHART_PURPLE = '#a78bfa';

// Darker purple UI accent (solid toggle/badge backgrounds needing white-text contrast) —
// distinct from the lighter CHART_PURPLE data-viz tint above. Pre-#381 value, centralized
// here byte-identical (was duplicated as local `LGM_PURPLE` consts in several dashboard files).
export const LGM_PURPLE_DARK = '#662D91';

// Chart-only low-end heatmap/visualMap tint (light orange). Distinct from LGM_ORANGE_TINT
// (a UI-chrome tint) — this is specifically the low end of a data-visualization gradient.
export const CHART_HEATMAP_LOW = '#FEE5D9';

// ─── Chart palette ───────────────────────────────────────────────────────────
// Ordered for visual distinction in multi-series charts. No LGM_MAGENTA (wordmark-only).
export const CHART_COLORS = [
  LGM_ORANGE,
  LGM_TEAL,
  CHART_PURPLE,
  COLOR_WARNING,
  COLOR_INFO,
] as const;

// ─── Button role ramps (rest / hover / pressed / disabled) ────────────────
// Best-guess ramps (see issue #381, Decision 4). Primary matches today's
// LgmButton exactly — zero visual regression for uncustomized call sites.
export const BUTTON_PRIMARY = {
  rest: '#2563eb',
  hover: '#1e40af',
  pressed: '#1e3a8a',
  contrastText: '#ffffff',
  // Matches the pre-existing LgmButton default disabled visual exactly —
  // zero regression for uncustomized call sites (see existing unit tests).
  disabledBg: '#9ca3af',
  disabledText: '#ffffff',
};

export const BUTTON_SECONDARY = {
  rest: '#9ca3af',
  hover: LGM_MUTED,
  pressed: LGM_SLATE,
  contrastText: LGM_INK, // #9ca3af is too light for white text to pass contrast
  disabledBg: LGM_LINE,
  disabledText: '#9ca3af',
};

export const BUTTON_SAVE = {
  rest: '#059669',
  hover: '#047857',
  pressed: '#065f46',
  contrastText: '#ffffff',
  disabledBg: COLOR_SUCCESS_BG,
  disabledText: COLOR_SUCCESS_TEXT,
};

export const BUTTON_DANGER = {
  rest: COLOR_DANGER,
  hover: '#dc2626',
  pressed: '#b91c1c',
  contrastText: '#ffffff',
  disabledBg: COLOR_DANGER_BG,
  disabledText: COLOR_DANGER_TEXT,
};

// Matches the Edit action icon color in the grids' Actions column
// (DataGridShared.tsx's `.MuiButtonBase-root:nth-of-type(2)` rule) — same
// rest/hover pair, so the modal header's Edit control reads as the same
// action as the grid row's Edit icon.
export const BUTTON_WARNING = {
  rest: '#d97706',
  hover: COLOR_WARNING_TEXT,
  pressed: '#78350f',
  contrastText: '#ffffff',
  disabledBg: COLOR_WARNING_BG,
  disabledText: COLOR_WARNING_TEXT,
};
